import { trackGameFunnel, trackSiteVisit, trackSiteVisitTime, type VisitFlags } from "@/lib/analytics";
import { gameForPath } from "@/lib/tracking/game-funnel";
import { localDay, weekOf } from "@/lib/tracking/local-day";
import { classifyVisitSource, type VisitSource } from "@/lib/tracking/visit-source";

// Visitas por país (métricas internas, /admin/estadisticas): una visita por
// pestaña y el tiempo que la página estuvo VISIBLE, enviado al ocultarla o
// cerrarla. Anónimo y sin cookies: el país lo pone Cloudflare en el server y
// acá solo queda en sessionStorage la marca "esta pestaña ya se contó"
// (no identifica a nadie), por eso se mide siempre, como las visitas de GA4.
const COUNTED_KEY = "cv_visit_counted";

// Nuevas vs recurrentes: este navegador guarda solo la fecha (día local) de
// su primera visita y el server recibe "ya había venido otro día" sí/no.
// Ningún identificador sale del navegador.
const FIRST_SEEN_KEY = "cv_first_seen";

// Personas por día y retorno por cohortes: el día de la última visita.
// Con eso el navegador avisa "primera visita de hoy / de esta semana".
const LAST_SEEN_KEY = "cv_last_seen";

// Lo que se sabe del dispositivo (todo vacío si el almacenamiento está
// bloqueado: no se sabe).
function deviceFlags(): VisitFlags {
  try {
    const today = localDay();
    let first = localStorage.getItem(FIRST_SEEN_KEY);
    if (!first) {
      first = today;
      localStorage.setItem(FIRST_SEEN_KEY, today);
    }
    const last = localStorage.getItem(LAST_SEEN_KEY);
    localStorage.setItem(LAST_SEEN_KEY, today);
    return {
      returning: first < today,
      firstOfDay: last !== today,
      firstOfWeek: !last || weekOf(last) !== weekOf(today),
      firstSeen: first,
    };
  } catch {
    return {};
  }
}

// Origen de la pestaña (solo en su primera página). Si llegó por un link
// compartido desde un juego, también cuenta en el embudo de ese juego y se
// borra la marca del # para que recargar no vuelva a sumarla.
function entrySource(): VisitSource {
  const source = classifyVisitSource({
    referrer: document.referrer,
    hash: location.hash,
    search: location.search,
    host: location.hostname,
  });
  if (source === "game_share") {
    const game = gameForPath(location.pathname);
    if (game) trackGameFunnel(game, "share_visit");
    history.replaceState(history.state, "", location.pathname + location.search);
  }
  return source;
}

// Tras tanto rato oculta, volver a la pestaña es una visita nueva (mismo
// criterio que el server, que deja de aceptar tiempo pasada esta ventana).
const NEW_VISIT_AFTER_MS = 30 * 60 * 1000;

// Menos de esto no aporta al promedio (pestañas que se abren y cierran).
const MIN_SEND_MS = 1000;

function alreadyCounted(): boolean {
  try {
    if (sessionStorage.getItem(COUNTED_KEY)) return true;
    sessionStorage.setItem(COUNTED_KEY, "1");
  } catch {
    // Almacenamiento bloqueado: se cuenta por carga de página.
  }
  return false;
}

// Devuelve la función que deja de medir (cleanup de useEffect).
export function startSiteVisitTracking(): () => void {
  if (!alreadyCounted()) trackSiteVisit({ ...deviceFlags(), source: entrySource() });

  let visibleSince: number | null = document.visibilityState === "visible" ? Date.now() : null;
  let hiddenAt: number | null = visibleSince === null ? Date.now() : null;

  function flush(): void {
    if (visibleSince === null) return;
    const ms = Date.now() - visibleSince;
    visibleSince = null;
    hiddenAt = Date.now();
    if (ms >= MIN_SEND_MS) trackSiteVisitTime(ms);
  }

  function onVisibilityChange(): void {
    if (document.visibilityState === "hidden") {
      flush();
      return;
    }
    if (visibleSince !== null) return;
    // Volver a la pestaña: visita nueva sin origen (no se sabe de dónde
    // volvió), pero sí puede ser la primera de un día nuevo.
    if (hiddenAt !== null && Date.now() - hiddenAt >= NEW_VISIT_AFTER_MS) trackSiteVisit(deviceFlags());
    visibleSince = Date.now();
    hiddenAt = null;
  }

  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("pagehide", flush);
  return () => {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("pagehide", flush);
    flush();
  };
}
